import { useEffect, useMemo, useState } from "react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Activity,
  AlertTriangle,
  Brain,
  FileSearch,
  Plus,
  Server,
  Shield,
  User,
  UserCheck,
  X,
} from "lucide-react";
import API from "../lib/api";

const AGENT_ICONS = {
  UserCheck,
  AlertTriangle,
  FileSearch,
  Shield,
  Server,
  User,
  Activity,
};

function buildAgent(persona, existingAgent = {}) {
  return {
    agent_id: persona.agent_id,
    name: existingAgent.name || persona.name,
    role: existingAgent.role || persona.role,
    icon: existingAgent.icon || persona.icon,
    description: existingAgent.description || persona.description || "",
    persona_summary:
      existingAgent.persona_summary ||
      existingAgent.description ||
      persona.description ||
      "",
    behavior_instructions: existingAgent.behavior_instructions || "",
    specialty_areas: Array.isArray(existingAgent.specialty_areas)
      ? existingAgent.specialty_areas
      : Array.isArray(persona.specialty_areas)
        ? persona.specialty_areas
        : [],
    system_prompt: existingAgent.system_prompt || persona.system_prompt || "",
    status: existingAgent.status || "idle",
  };
}

function buildConfigFromTemplate(templateId, templates, personas, seedConfig) {
  const template =
    templates.find((entry) => entry.id === templateId) || templates[0] || null;
  const personaMap = new Map(personas.map((persona) => [persona.agent_id, persona]));
  const sourceAgents = seedConfig?.agents?.length
    ? seedConfig.agents
    : (template?.agents || []).map((agentId) => ({ agent_id: agentId }));

  return {
    template_id: seedConfig?.template_id || "custom",
    template_name:
      seedConfig?.template_name ||
      (template ? `Custom ${template.name}` : "Custom Audit Team"),
    base_template_id:
      seedConfig?.base_template_id || template?.id || templateId || "balanced",
    risk_appetite:
      seedConfig?.risk_appetite || template?.risk_appetite || "balanced",
    customized: true,
    agents: sourceAgents
      .map((agent) => {
        const persona = personaMap.get(agent.agent_id);
        return persona ? buildAgent(persona, agent) : null;
      })
      .filter(Boolean),
  };
}

export default function TeamConfigEditor({
  initialTeamConfig,
  onSave,
  onCancel,
  saveLabel = "Save Team",
}) {
  const [templates, setTemplates] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [{ data: templateData }, { data: personaData }] = await Promise.all([
          API.get("/team-templates"),
          API.get("/agent-personas"),
        ]);
        const nextTemplates = templateData.templates || [];
        const nextPersonas = personaData.agents || [];
        setTemplates(nextTemplates);
        setPersonas(nextPersonas);
        setConfig(
          buildConfigFromTemplate(
            initialTeamConfig?.base_template_id ||
              initialTeamConfig?.template_id ||
              "balanced",
            nextTemplates,
            nextPersonas,
            initialTeamConfig
          )
        );
      } catch (error) {
        console.error("Failed to load team configuration data:", error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [initialTeamConfig]);

  const availablePersonas = useMemo(
    () =>
      personas.filter(
        (persona) =>
          !config?.agents?.some((agent) => agent.agent_id === persona.agent_id)
      ),
    [config?.agents, personas]
  );

  const resetFromTemplate = () => {
    setConfig(
      buildConfigFromTemplate(
        config?.base_template_id || "balanced",
        templates,
        personas,
        null
      )
    );
  };

  const updateAgent = (agentId, field, value) => {
    setConfig((current) => ({
      ...(current || {}),
      agents: (current?.agents || []).map((agent) =>
        agent.agent_id === agentId ? { ...agent, [field]: value } : agent
      ),
    }));
  };

  const removeAgent = (agentId) => {
    setConfig((current) => ({
      ...(current || {}),
      agents: (current?.agents || []).filter((agent) => agent.agent_id !== agentId),
    }));
  };

  const addAgent = (agentId) => {
    const persona = personas.find((entry) => entry.agent_id === agentId);
    if (!persona) return;
    setConfig((current) => ({
      ...(current || {}),
      agents: [...(current?.agents || []), buildAgent(persona)],
    }));
  };

  if (loading || !config) {
    return <div className="py-6 text-sm text-muted-foreground">Loading team editor...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-3">
          <div>
            <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Team Name
            </Label>
            <Input
              value={config.template_name || ""}
              onChange={(e) =>
                setConfig((current) => ({ ...(current || {}), template_name: e.target.value }))
              }
            />
          </div>
          <div>
            <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Risk Appetite
            </Label>
            <div className="flex flex-wrap gap-2">
              {["conservative", "balanced", "aggressive"].map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() =>
                    setConfig((current) => ({ ...(current || {}), risk_appetite: option }))
                  }
                  className={`rounded-full border px-3 py-1 text-xs transition-all ${
                    config.risk_appetite === option
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border text-muted-foreground hover:border-primary/40"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>
        <Card className="bg-muted/20">
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Add Specialist</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 py-2">
            {availablePersonas.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                All available personas are already included.
              </p>
            ) : (
              availablePersonas.map((persona) => (
                <button
                  key={persona.agent_id}
                  type="button"
                  onClick={() => addAgent(persona.agent_id)}
                  className="flex w-full items-center justify-between rounded-lg border border-border bg-background/40 px-3 py-2 text-left transition-all hover:border-primary/40 hover:bg-primary/5"
                >
                  <div>
                    <p className="text-sm font-medium">{persona.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {persona.role.replaceAll("_", " ")}
                    </p>
                  </div>
                  <Plus className="h-4 w-4 text-muted-foreground" />
                </button>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-3">
        {(config.agents || []).map((agent) => {
          const IconComponent = AGENT_ICONS[agent.icon] || Brain;
          return (
            <Card key={agent.agent_id} className="bg-muted/20">
              <CardHeader className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg border border-primary/20 bg-primary/10 p-2">
                      <IconComponent className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-sm">{agent.name}</CardTitle>
                      <p className="text-xs text-muted-foreground">
                        {agent.role.replaceAll("_", " ")}
                      </p>
                    </div>
                  </div>
                  {agent.agent_id !== "lead_auditor" && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAgent(agent.agent_id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Agent Name
                    </Label>
                    <Input
                      value={agent.name}
                      onChange={(e) => updateAgent(agent.agent_id, "name", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Specialty Areas
                    </Label>
                    <Input
                      value={(agent.specialty_areas || []).join(", ")}
                      onChange={(e) =>
                        updateAgent(
                          agent.agent_id,
                          "specialty_areas",
                          e.target.value
                            .split(",")
                            .map((item) => item.trim())
                            .filter(Boolean)
                        )
                      }
                    />
                  </div>
                </div>
                <div>
                  <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Persona Summary
                  </Label>
                  <Textarea
                    value={agent.persona_summary || ""}
                    onChange={(e) =>
                      updateAgent(agent.agent_id, "persona_summary", e.target.value)
                    }
                    className="min-h-[72px]"
                  />
                </div>
                <div>
                  <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Behavior Instructions
                  </Label>
                  <Textarea
                    value={agent.behavior_instructions || ""}
                    onChange={(e) =>
                      updateAgent(
                        agent.agent_id,
                        "behavior_instructions",
                        e.target.value
                      )
                    }
                    className="min-h-[72px]"
                  />
                </div>
                <div>
                  <Label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    System Prompt
                  </Label>
                  <Textarea
                    value={agent.system_prompt || ""}
                    onChange={(e) =>
                      updateAgent(agent.agent_id, "system_prompt", e.target.value)
                    }
                    className="min-h-[180px] font-mono text-[11px]"
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex justify-between gap-2 border-t pt-4">
        <Button type="button" variant="outline" onClick={resetFromTemplate}>
          Reset From Template
        </Button>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!(config.agents || []).length}
            onClick={() => onSave?.(config)}
          >
            {saveLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
